import MobileGate from "@hl/base-components/lib/MobileGate";
import { ProtectedRoute } from "@hl/shared-features/lib/features/auth/ProtectedRoute";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import { NotFound } from "@hl/shared-features/lib/features/layout/NotFound";
import { Routes as ReactRoutes, Route } from "react-router-dom";

import { TOOLS_URL } from "config";
import EmailSettings from "~/features/account/email/EmailSettings";
import { EmailVerify } from "~/features/account/email/EmailVerify";
import CollectionsDashboard from "~/features/collections/sections/ContractCollectionDashboard/CollectionsDashboard";
import ContractsDashboardV2 from "~/features/collections/sections/ContractCollectionDashboard/ContractsDashboardV2";
import { CollectionsDashboard as CollectionsDashboardV3 } from "~/features/collections-dashboard/CollectionsDashboard";
import ContractDetail from "~/features/contracts/ContractDetail";
import DashboardLayout from "~/features/dashboard/DashboardLayout";
import ManagePage from "~/features/pages/ManagePage";

import CreateTokenWizard2 from "./features/collections/sections/CreateTokenWizard";
import { CurationsDashboard } from "./features/curations/CurationsDashboard";
import { EditCuration } from "./features/curations/EditCuration";
import { PreviewCuration } from "./features/curations/PreviewCuration";
import MainLayout from "./features/dashboard/MainLayout";
import DebugDashboard from "./features/debug/index";
import { CreateGate, EditGate } from "./features/gates";
import GatesDashboardV2 from "./features/gates/GatesDashboardV2";
import { ManageCollection as ManageCollectionV3 } from "./features/manage-collection/ManageCollection";
import EditPage from "./features/pages/EditPage";
import PagesDashboardV2 from "./features/pages/PagesDashboardV2";
import { ManageCollection } from "./features/tokens/sections/ManageToken/ManageToken";

export const Routes = () => {
  const showAccountSettings = useFeatureFlags(
    FEATURE_FLAGS.SHOW_ACCOUNT_SETTINGS_ON_FRONTEND
  );
  const enableV3API = useFeatureFlags(FEATURE_FLAGS.ENABLE_V3_API);
  const showDebugPages = useFeatureFlags(FEATURE_FLAGS.SHOW_DEBUG_PAGES);

  return (
    <ReactRoutes>
      <Route path={TOOLS_URL.home} element={<DashboardLayout />}>
        <Route
          index
          element={
            <ProtectedRoute message="Sign In to create a collection or see any collections you’ve already created.">
              {enableV3API ? (
                <CollectionsDashboardV3 />
              ) : (
                <CollectionsDashboard />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path={TOOLS_URL.contract.list}
          element={
            <ProtectedRoute message="Sign In to see any contracts you’ve already created.">
              <MobileGate>
                <ContractsDashboardV2 />
              </MobileGate>
            </ProtectedRoute>
          }
        />
        <Route
          path={TOOLS_URL.pages.list}
          element={
            <ProtectedRoute message="Sign In to create a page or see any pages you’ve already created.">
              <MobileGate>
                <PagesDashboardV2 />
              </MobileGate>
            </ProtectedRoute>
          }
        />
        <Route
          path={TOOLS_URL.gates.list}
          element={
            <ProtectedRoute message="Sign In to create a gate or see any gates you’ve already created.">
              <GatesDashboardV2 />
            </ProtectedRoute>
          }
        />
        <Route
          path={TOOLS_URL.curations.list}
          element={
            <ProtectedRoute>
              <MobileGate>
                <CurationsDashboard />
              </MobileGate>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path={TOOLS_URL.gates.create}
        element={
          <ProtectedRoute>
            <CreateGate />
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.gates.edit}
        element={
          <ProtectedRoute>
            <EditGate />
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.collections.create}
        element={
          <ProtectedRoute>
            <MobileGate>
              <CreateTokenWizard2 />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.collections.createType}
        element={
          <ProtectedRoute>
            <MobileGate>
              <CreateTokenWizard2 />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.collections.resumeCreate}
        element={
          <ProtectedRoute>
            <MobileGate>
              <CreateTokenWizard2 />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.collections.manage}
        element={
          <ProtectedRoute>
            <MobileGate>
              {enableV3API ? <ManageCollectionV3 /> : <ManageCollection />}
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.pages.create}
        element={
          <ProtectedRoute>
            <MobileGate>
              <ManagePage />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.pages.edit}
        element={
          <ProtectedRoute>
            <MobileGate>
              <EditPage />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      {showAccountSettings && (
        <>
          <Route
            path={TOOLS_URL.account.settings.email}
            element={
              <ProtectedRoute>
                <MobileGate>
                  <EmailSettings />
                </MobileGate>
              </ProtectedRoute>
            }
          />
          <Route
            path={TOOLS_URL.account.settings.emailVerify}
            element={<EmailVerify />}
          />
        </>
      )}
      {showDebugPages && (
        <Route
          path={TOOLS_URL.debugRoot}
          element={
            <MobileGate>
              <MainLayout />
            </MobileGate>
          }
        >
          <Route
            index
            element={
              <ProtectedRoute>
                <MobileGate>
                  <DebugDashboard />
                </MobileGate>
              </ProtectedRoute>
            }
          />
        </Route>
      )}
      <Route
        path={TOOLS_URL.contract.detail}
        element={
          <ProtectedRoute>
            <MobileGate>
              <ContractDetail />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.curations.edit}
        element={
          <ProtectedRoute>
            <MobileGate>
              <EditCuration />
            </MobileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path={TOOLS_URL.curations.preview}
        element={
          <ProtectedRoute>
            <PreviewCuration />
          </ProtectedRoute>
        }
      />
      <Route path={"*"} element={<NotFound redirectUrl={TOOLS_URL.home} />} />
    </ReactRoutes>
  );
};
